import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Tooltip.css';

/**
 * Shared, dependency-free tooltip wrapper (Requirement 21).
 *
 * Renders its single child (typically an icon button) plus a positioned
 * tooltip bubble. Visibility is driven by local state that is toggled from
 * both hover/focus (desktop) and tap/click (mobile), because CSS `:hover`
 * and the `title` attribute are unreliable on touch devices.
 *
 * Contract:
 * - Shows on `mouseenter`/`focus`, hides on `mouseleave`/`blur` (Req 21.2).
 * - Toggles on click/tap so the label is reachable without hover (Req 21.3).
 * - The bubble is rendered with `role="tooltip"` only while open.
 * - Closes on Escape and on an outside tap (nice-to-have).
 * - Does NOT override the child's `onClick` and does NOT set an `aria-label`
 *   on the child — every button that uses `Tooltip` carries its own
 *   `aria-label` (the accessible name) (Req 21.1).
 *
 * @param {Object}   props
 * @param {string}   props.label     Tooltip text.
 * @param {React.ReactNode} props.children The wrapped element.
 * @param {('top'|'bottom'|'left'|'right')} [props.position='top'] Bubble placement.
 */
function Tooltip({ label, children, position = 'top' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const show = useCallback(() => setOpen(true), []);
  const hide = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  // Close on Escape and on an outside tap/click while open.
  useEffect(() => {
    if (!open) return undefined;

    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleKey);
    document.addEventListener('pointerdown', handleOutside);
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.removeEventListener('pointerdown', handleOutside);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className="tooltip-wrap"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={toggle}
    >
      {children}
      {open && (
        <span
          className={`tooltip-bubble tooltip-bubble--${position}`}
          role="tooltip"
        >
          {label}
        </span>
      )}
    </span>
  );
}

export default Tooltip;
